import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import ListIcon from '@mui/icons-material/List';
import DescriptionIcon from '@mui/icons-material/Description';
import VerifiedIcon from '@mui/icons-material/Verified';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import { toast } from 'react-toastify';
import { useAuth } from 'context/AuthContext';
import { authApi } from 'utils/api';

const DRAWER_WIDTH = 200;

const menuItems = [
    { label: '발급 목록', path: '/', icon: <ListIcon /> },
    { label: '라이선스 발급', path: '/generate', icon: <DescriptionIcon /> },
    { label: '라이선스 검증', path: '/verify', icon: <VerifiedIcon /> },
];

const adminMenuItem = { label: '관리', path: '/admin', icon: <SettingsIcon /> };

export default function Layout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAdmin, logout } = useAuth();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [mobileOpen, setMobileOpen] = useState(false);

    // 비밀번호 변경 다이얼로그
    const [pwOpen, setPwOpen] = useState(false);
    const [currentPw, setCurrentPw] = useState('');
    const [newPw, setNewPw] = useState('');
    const [confirmPw, setConfirmPw] = useState('');
    const [pwSubmitting, setPwSubmitting] = useState(false);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleOpenPwDialog = () => {
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
        setPwOpen(true);
    };

    const handleChangePw = async (e) => {
        e.preventDefault();

        if (newPw !== confirmPw) {
            toast.error('새 비밀번호가 일치하지 않습니다.');
            return;
        }

        if (newPw.length < 4) {
            toast.error('새 비밀번호는 4자 이상이어야 합니다.');
            return;
        }

        setPwSubmitting(true);
        try {
            await authApi.changePassword(currentPw, newPw);
            toast.success('비밀번호가 변경되었습니다.');
            setPwOpen(false);
        } catch (err) {
            toast.error(err.response?.data?.error || '비밀번호 변경 실패');
        } finally {
            setPwSubmitting(false);
        }
    };

    const handleNavClick = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const allMenuItems = isAdmin ? [...menuItems, adminMenuItem] : menuItems;

    const drawerContent = (
        <>
            <Toolbar />
            <List>
                {allMenuItems.map((item) => (
                    <ListItemButton
                        key={item.path}
                        selected={location.pathname === item.path}
                        onClick={() => handleNavClick(item.path)}
                    >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                    </ListItemButton>
                ))}
            </List>
        </>
    );

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: 'background.paper',
                }}
                elevation={1}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isMobile && (
                            <IconButton
                                edge="start"
                                onClick={() => setMobileOpen(!mobileOpen)}
                                sx={{ color: 'text.primary' }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Typography
                            variant="h1"
                            sx={{
                                fontWeight: 600,
                                fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                            }}
                            noWrap
                        >
                            {isMobile ? '라이선스 시스템' : '인투소프트 라이선스 시스템'}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Typography
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.875rem',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                                display: { xs: 'none', sm: 'block' },
                            }}
                            onClick={handleOpenPwDialog}
                            title="비밀번호 변경"
                        >
                            {user?.username} ({user?.role})
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleLogout}
                        >
                            {isMobile ? '로그아웃' : '로그아웃'}
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* 모바일: 임시 드로어 / 데스크톱: 고정 드로어 */}
            {isMobile ? (
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            bgcolor: 'background.paper',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            ) : (
                <Drawer
                    variant="permanent"
                    sx={{
                        width: DRAWER_WIDTH,
                        flexShrink: 0,
                        '& .MuiDrawer-paper': {
                            width: DRAWER_WIDTH,
                            boxSizing: 'border-box',
                            bgcolor: 'background.paper',
                            borderRight: 1,
                            borderColor: 'divider',
                        },
                    }}
                >
                    {drawerContent}
                </Drawer>
            )}

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: { xs: 1.5, sm: 2, md: 3 },
                    width: { xs: '100%', md: `calc(100% - ${DRAWER_WIDTH}px)` },
                    overflow: 'hidden',
                }}
            >
                <Toolbar />
                <Outlet />
            </Box>

            {/* 비밀번호 변경 다이얼로그 */}
            <Dialog open={pwOpen} onClose={() => setPwOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>비밀번호 변경</DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        id="change-pw-form"
                        onSubmit={handleChangePw}
                        sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
                    >
                        <TextField
                            label="현재 비밀번호"
                            type="password"
                            value={currentPw}
                            onChange={(e) => setCurrentPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            autoFocus
                        />
                        <TextField
                            label="새 비밀번호"
                            type="password"
                            value={newPw}
                            onChange={(e) => setNewPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            helperText="4자 이상"
                        />
                        <TextField
                            label="새 비밀번호 확인"
                            type="password"
                            value={confirmPw}
                            onChange={(e) => setConfirmPw(e.target.value)}
                            required
                            size="small"
                            fullWidth
                            error={confirmPw !== '' && newPw !== confirmPw}
                            helperText={confirmPw !== '' && newPw !== confirmPw ? '비밀번호가 일치하지 않습니다' : ''}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPwOpen(false)}>취소</Button>
                    <Button
                        type="submit"
                        form="change-pw-form"
                        variant="contained"
                        disabled={pwSubmitting}
                    >
                        변경
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
